import {
    ActionReducerMapBuilder,
    Draft,
    SerializedError,
    createAsyncThunk,
    createSlice,
} from '@reduxjs/toolkit';
import { IAction, IAppReduxStore, SliceProps } from '../../store';
import {
    urlEmailTemplates,
    urlParamReplicaId,
    urlProjectDocumentsShareEmail,
} from '../../../Constants';
import { ExportConfigEnum, getExportConfig } from './sliceProjectAdvancedExport';
import { ISelectTemplate } from '../../../components/General/Dialogs/_parts/SelectTemplate';
import { sortTemplates } from '../../../util/TemplateUtils';
import { IExportAdditionalDocument } from '../../../components/General/Dialogs/_parts/AdvancedExport';
import { apiFetch } from '../../../util/ProjectUtils';

/**
 * Initial slice state value.
 */
const initialState: IProjectDocumentsShareEmailState = {
    isDialogOpen: false,
    isEmailSent: false,
    loading: false,
    error: null,
    templates: null,
};

const reducers = {
    openShareEmailDialog(state: IProjectDocumentsShareEmailState) {
        state.isDialogOpen = true;
    },

    closeShareEmailDialog(state: IProjectDocumentsShareEmailState) {
        state.isDialogOpen = false;
        state.isEmailSent = false;
    },
};
/**
 * Collection of the thunks for this slice, along with their reducer functions.
 */
const thunks = {
    /**
     * Get email templates for Upload Additional Documents Dialog
     */
    getEmailTemplatesForShareEmail: async (config: {
        replicaId: string;
    }): Promise<{ templates: IEmailTemplate[] }> => {
        try {
            const { replicaId } = config;
            const request: RequestInit = {
                headers: {
                    replicaId,
                },
            };

            const response = await apiFetch(urlEmailTemplates, request);

            return response.ok
                ? Promise.resolve(await response.json())
                : Promise.reject(new Error(await response.text()));
        } catch (e) {
            return Promise.reject(e as Error);
        }
    },

    onConfirmShareEmail: async (
        config: {
            replicaId: string;
            mailSendOption: MailSendOptionEnum;
            sendTo: string[];
            blindCopyTo: string[];
            copyTo: string[];
            subject: string;
            emailText: string;
            replyTo: string[];
            additionalFiles;
            includeInExport;
            documentTypeName;
        },
        thunkApi,
    ): Promise<void> => {
        const {
            replicaId,
            mailSendOption,
            sendTo,
            blindCopyTo,
            copyTo,
            subject,
            emailText,
            replyTo,
            additionalFiles,
            includeInExport,
            documentTypeName,
        } = config;

        const exportConfig = getExportConfig({
            additionalFiles,
            includeInExport,
            documentTypeName,
        });
        const { data: documents } = (thunkApi.getState() as IAppReduxStore).projectDocuments.info;

        try {
            const formData = new FormData();
            formData.append('universalIds', documents.map(item => item.universalId).join('|'));
            formData.append(
                'config',
                JSON.stringify({
                    replyTo: replyTo.join(';'),
                    blindCopyTo: blindCopyTo.join(';'),
                    copyTo: copyTo.join(';'),
                    subject,
                    body: emailText,
                    sendTo: sendTo.join(';'),
                    mailSendOption: mailSendOption.toString(),
                    ...exportConfig,
                }),
            );

            const request: RequestInit = {
                method: 'POST',
                body: formData,
            };

            const url = urlProjectDocumentsShareEmail.replace(urlParamReplicaId, replicaId);
            const response = await apiFetch(url, request);

            return response.ok
                ? Promise.resolve(await response.json())
                : Promise.reject(new Error(await response.text()));
        } catch (e) {
            return Promise.reject(e as Error);
        }
    },

    /**
     * Registers the 'extraReducers' functions for the slice's thunks.
     */
    _reducers: (builder: ActionReducerMapBuilder<IProjectDocumentsShareEmailState>) => {
        /**
         * Reducers for the 'getEmailTemplatesForShareEmail' thunk.
         */
        builder.addCase(
            getEmailTemplatesForShareEmail.pending,
            (state: Draft<IProjectDocumentsShareEmailState>) => {
                state.loading = true;
                state.error = null;
            },
        );

        builder.addCase(
            getEmailTemplatesForShareEmail.fulfilled,
            (
                state: Draft<IProjectDocumentsShareEmailState>,
                action: IAction<{ templates: IEmailTemplate[] }>,
            ) => {
                state.templates = sortTemplates(action.payload.templates);

                state.loading = false;
                state.error = null;
            },
        );

        builder.addCase(
            getEmailTemplatesForShareEmail.rejected,
            (state: Draft<IProjectDocumentsShareEmailState>, action: IAction<SerializedError>) => {
                state.loading = false;
                state.error = action.error;
            },
        );

        /**
         * Reducers for the 'onConfirmShareEmail' thunk.
         */
        builder.addCase(
            onConfirmShareEmail.pending,
            (state: Draft<IProjectDocumentsShareEmailState>) => {
                state.loading = true;
                state.error = null;
            },
        );

        builder.addCase(
            onConfirmShareEmail.fulfilled,
            (state: Draft<IProjectDocumentsShareEmailState>) => {
                state.isEmailSent = true;

                state.loading = false;
                state.error = null;
            },
        );

        builder.addCase(
            onConfirmShareEmail.rejected,
            (state: Draft<IProjectDocumentsShareEmailState>, action: IAction<SerializedError>) => {
                state.loading = false;
                state.error = action.error;
            },
        );
    },
};

export const onConfirmShareEmail = createAsyncThunk(
    `${urlProjectDocumentsShareEmail}`,
    thunks.onConfirmShareEmail,
);

export const getEmailTemplatesForShareEmail = createAsyncThunk(
    urlEmailTemplates,
    thunks.getEmailTemplatesForShareEmail,
);

export enum MailSendOptionEnum {
    FileAttachment = '1',
    DownloadLink = '2',
    EPLASSFileCloud = '3',
}

export const ContactPersonKey = '#ContactPerson_MailAddress';

export interface IEmailTemplate extends ISelectTemplate {
    blindCopyTo: string[];
    body: string;
    copyTo: string[];
    mailSendOption: MailSendOptionEnum;
    replyTo: string[];
    sendTo: string[];
    [ExportConfigEnum.ExportAdditionalDocument]: IExportAdditionalDocument[];
    [ExportConfigEnum.ExportDatamodelOriginal]: string;
    [ExportConfigEnum.ExportExcelReport]: string;
    [ExportConfigEnum.ExportFileOriginal]: string;
    [ExportConfigEnum.ExportFileRedliningPdf]: string;
    [ExportConfigEnum.ExportMailReport]: string[];
    [ExportConfigEnum.ExportNote]: string;
    [ExportConfigEnum.ExportProtocol]: string;
    [ExportConfigEnum.ExportRedliningReport]: string[];
    [ExportConfigEnum.FormatFileName]: string;
}

/**
 * Interface that describes the state object for this redux slice.
 */
export interface IProjectDocumentsShareEmailState extends SliceProps {
    isDialogOpen: boolean;
    isEmailSent: boolean;
    templates: IEmailTemplate[];
}

export const defaultEmailTemplate: IEmailTemplate = {
    blindCopyTo: [''],
    body: '',
    copyTo: [],
    docUNID: 'default',
    mailSendOption: MailSendOptionEnum.DownloadLink,
    replyTo: [],
    sendTo: [],
    subject: '',
    visibility: null,
    [ExportConfigEnum.ExportAdditionalDocument]: [],
    [ExportConfigEnum.ExportDatamodelOriginal]: '',
    [ExportConfigEnum.ExportExcelReport]: '',
    [ExportConfigEnum.ExportFileOriginal]: '',
    [ExportConfigEnum.ExportFileRedliningPdf]: '',
    [ExportConfigEnum.ExportMailReport]: [],
    [ExportConfigEnum.ExportNote]: '',
    [ExportConfigEnum.ExportProtocol]: '',
    [ExportConfigEnum.ExportRedliningReport]: [],
    [ExportConfigEnum.FormatFileName]: '',
};

/**
 * Create the redux slice.
 */
export const projectShareEmailDataSlice = createSlice({
    name: 'projectShareEmailData',
    extraReducers: thunks._reducers,
    initialState,
    reducers,
});

export const getTemplates = (state: IAppReduxStore) => {
    return [defaultEmailTemplate, ...(state.projectDocumentsShareEmail.templates || [])];
};
/**
 * Export functions from slice actions.
 */
export const { openShareEmailDialog, closeShareEmailDialog } = projectShareEmailDataSlice.actions;

/**
 * Export slice reducer as default.
 */
export default projectShareEmailDataSlice.reducer;
